export type AuthStatus = 'unconfigured' | 'anonymous' | 'authenticated';

export interface AuthAccount {
  id: string;
  name: string | null;
  email: string | null;
}

/** The session, with MSAL (or the mock session) behind it. Every interactive step is a redirect. */
export interface AuthApi {
  readonly status: AuthStatus;
  readonly account: AuthAccount | null;
  /** Sign-in redirect; `/auth/callback` then returns to `returnTo`, a path on this site (default `/`). */
  signIn(returnTo?: string): Promise<void>;
  /**
   * Sign-up redirect (`prompt=create`), only ever after the pre-screen. `returnOrigin` is an
   * allow-listed app origin to continue to once the account exists.
   */
  createAccount(returnOrigin: string | null): Promise<void>;
  /** A fresh sign-in (`prompt=login`) that returns to `returnTo` — deleting the account needs one. */
  reauthenticate(returnTo: string): Promise<void>;
  /** Ends the session here and at the sign-in service, then lands on /signed-out. */
  signOut(): Promise<void>;
  /** Forgets the session in this browser tab, without a redirect (/signed-out). */
  forgetSession(): Promise<void>;
  /**
   * Access token for the account API, or null when there is no session — or when the session can
   * no longer be renewed silently, in which case a sign-in redirect back to this page has started.
   */
  getAccessToken(): Promise<string | null>;
}

/** Used when the build has no Entra configuration (no VITE_ENTRA_* values). */
export const unconfiguredAuth: AuthApi = {
  status: 'unconfigured',
  account: null,
  signIn: () => Promise.resolve(),
  createAccount: () => Promise.resolve(),
  reauthenticate: () => Promise.resolve(),
  signOut: () => Promise.resolve(),
  forgetSession: () => Promise.resolve(),
  getAccessToken: () => Promise.resolve(null),
};
